// "Allow the assistant to read this": a folder, a document or a selection,
// for zehn-aip to read with nobody present, by a capability grant docs issues
// from the person's own session (../libraryGrants.js). The dialog lists the
// live grants on what is selected, issues a new one for the time chosen
// (`shares:write`), and revokes any the person may revoke (docs decides: the
// issuer, or whoever may share an object it names). aip collects the grant
// at docs itself; nothing is handed to it from here.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Loader2, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { iHold } from '@/shared/auth/me.js';
import { SCOPE } from '@/shared/auth/scopes.js';
import { apiErrorMessage } from '@/shared/utils/apiError';
import { formatDateTime } from '@/shared/utils/formatDate';
import { DEFAULT_TTL, TTL_CHOICES, issueGrant, listGrants, revokeGrant } from '../libraryGrants';

const MAX_NAMES = 5;

/**
 * @param {Array<{uuid, name, type}>|null} nodes  what to allow (null or empty closes the dialog)
 * @param {function} onOpenChange
 */
export function AllowAssistantDialog({ nodes, onOpenChange }) {
  const { t, i18n } = useTranslation(['assistant', 'common']);
  const open = Array.isArray(nodes) && nodes.length > 0;
  const [grants, setGrants] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [ttl, setTtl] = useState(DEFAULT_TTL);
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(null);
  // Issuing, and revoking someone else's grant, are `shares:write`'s; the
  // issuer revokes their own with `files:read` alone.
  const mayShare = iHold(SCOPE.SHARES_WRITE);

  const load = useCallback(async () => {
    if (!open) return;
    setLoadError(null);
    try {
      setGrants(await listGrants(nodes));
    } catch (error) {
      setGrants([]);
      setLoadError(apiErrorMessage(error));
    }
  }, [open, nodes]);

  useEffect(() => {
    setGrants(null);
    setTtl(DEFAULT_TTL);
    void load();
  }, [load]);

  const allow = async () => {
    setBusy(true);
    try {
      await issueGrant(nodes, ttl);
      toast.success(t('assistant:grants.allowed', { count: nodes.length }));
      onOpenChange(false);
    } catch (error) {
      toast.error(t('assistant:grants.failed', { message: apiErrorMessage(error) }));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (grant) => {
    setRevoking(grant.id);
    try {
      await revokeGrant(grant.id);
      toast.success(t('assistant:grants.revoked'));
      await load();
    } catch (error) {
      toast.error(t('assistant:grants.failed', { message: apiErrorMessage(error) }));
    } finally {
      setRevoking(null);
    }
  };

  const names = open ? nodes.map((node) => node.name || node.uuid) : [];
  const shown = names.slice(0, MAX_NAMES).join(', ') + (names.length > MAX_NAMES ? ` +${names.length - MAX_NAMES}` : '');

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value && !busy) onOpenChange(false); }}>
      <DialogContent className="sm:max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="allow-assistant-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bot className="h-4 w-4" />
            {t('assistant:grants.title')}
          </DialogTitle>
          <DialogDescription>{t('assistant:grants.hint', { count: names.length })}</DialogDescription>
        </DialogHeader>

        <div className="text-sm font-medium break-words" data-testid="allow-assistant-objects">{shown}</div>

        <section className="space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground">{t('assistant:grants.current')}</h3>
          {grants === null ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : grants.length ? (
            <ul className="space-y-1.5" data-testid="allow-assistant-grants">
              {grants.map((grant) => (
                <li key={grant.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm" data-testid="allow-assistant-grant">
                  <div className="min-w-0 flex-1">
                    <div>{t('assistant:grants.until', { date: formatDateTime(grant.expiresAt, i18n.language) })}</div>
                    <div className="text-xs text-muted-foreground">
                      {grant.issuedByMe ? t('assistant:grants.byYou') : t('assistant:grants.by', { name: grant.issuedByName || grant.issuedBy })}
                      {' · '}
                      {t('assistant:grants.objects', { count: (grant.objects || []).length })}
                    </div>
                  </div>
                  {(grant.issuedByMe || mayShare) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-red-600 hover:text-red-600"
                      disabled={revoking === grant.id}
                      onClick={() => void revoke(grant)}
                      data-testid="allow-assistant-revoke"
                    >
                      {revoking === grant.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
                      {t('assistant:grants.revoke')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="allow-assistant-none">{t('assistant:grants.none')}</p>
          )}
          {loadError && <p className="text-xs text-red-600">{t('assistant:grants.loadFailed', { message: loadError })}</p>}
        </section>

        {mayShare && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground" htmlFor="allow-assistant-ttl">{t('assistant:grants.duration')}</label>
            <select
              id="allow-assistant-ttl"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={ttl}
              onChange={(e) => setTtl(Number(e.target.value))}
              data-testid="allow-assistant-ttl"
            >
              {TTL_CHOICES.map((days) => (
                <option key={days} value={days}>{t('assistant:grants.days', { count: days })}</option>
              ))}
            </select>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('common:cancel')}</Button>
          {mayShare && (
            <Button onClick={() => void allow()} disabled={busy} data-testid="allow-assistant-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('assistant:grants.allow')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
